import { useReadQuery } from "@apollo/client";
import { QueryRef } from "@apollo/client/react/types/types";
import { Box } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { TextCellWithTooltip } from "components/DataGridCell";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import {
  InvoiceAuditReportQuery,
  InvoiceAuditReportQueryVariables,
  InvoiceAuditReportSummaryQuery,
  InvoiceAuditReportSummaryQueryVariables,
} from "generated-graphql/graphql";
import { DataGridSkeleton } from "hooks/omnisearch-v2/DataGridSkeleton";
import { DataGridToolbar } from "hooks/omnisearch-v2/DataGridToolbar";
import { useOmnisearchActions, useOmnisearchStore } from "hooks/omnisearch-v2/omnisearchStore";
import { SuspenseDataGrid, SuspenseDataGridProps } from "hooks/omnisearch-v2/SuspenseDataGrid";
import { useGridPersistence } from "hooks/omnisearch-v2/useGridPersistence";
import { usePageForSearch } from "hooks/omnisearch-v2/usePageForSearch";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { ReactNode, Suspense, useCallback, useMemo } from "react";

import { WasteListPageSurface } from "../../WasteListPageSurface";
import { INVOICE_TRACKING_HEADER_HEIGHT } from "../InvoiceTrackingDateFilter";
import { InvoiceTrackingListHeading } from "../InvoiceTrackingListHeading";
import { usePeriodDisplayTokens } from "../invoiceTrackingPeriod";
import { formatInvoiceDate } from "../utils";
import { AuditErrorBoundary } from "./AuditErrorBoundary";
import { AuditSummary } from "./auditUi";
import { formatAuditMoney, formatAuditTotal } from "./formatAuditValues";
import { useInvoiceAuditReportQueries } from "./useInvoiceAuditQueries";

type ReportRow = InvoiceAuditReportQuery["wasteInvoiceChargeAuditReport"]["items"][number];
const INITIAL_SORT: GridSortModel = [{ field: "variance", sort: "desc" }];
const PERSISTENCE_KEY = "waste-invoice-audit-report";
const PAGE_SIZES = [25, 50, 100];
const FILTER_KEYS: FilterKey[] = [
  {
    key: "vendor",
    header: "Vendor",
    filterKeyType: "wasteInvoiceVendor",
  },
  { key: "invoiceNumber", header: "Invoice #" },
  { key: "facilityId", header: "Facility", filterKeyType: "facility" },
  { key: "billedAmount", header: "Billed amount", filterKeyType: "number" },
  { key: "nonCompliantLineCount", header: "Mismatch lines", filterKeyType: "number" },
  { key: "unresolvedLineCount", header: "Unresolved lines", filterKeyType: "number" },
  { key: "totalOverages", header: "Overages", filterKeyType: "number" },
  { key: "totalUnderages", header: "Underages", filterKeyType: "number" },
  { key: "variance", header: "Net variance", filterKeyType: "number" },
];
const PRESERVED_FILTER_KEYS = ["invoiceId", "vendorId", "invoiceDate"];
const FILTER_GROUPS: FilterGroup[] = [
  { name: "Invoice", fields: ["vendor", "invoiceNumber"] },
  { name: "Location", fields: ["facilityId"] },
  {
    name: "Audit",
    fields: [
      "billedAmount",
      "nonCompliantLineCount",
      "unresolvedLineCount",
      "totalOverages",
      "totalUnderages",
      "variance",
    ],
  },
];
const COLUMNS: OmnisearchGridColDef<ReportRow>[] = [
  {
    field: "invoiceDate",
    headerName: "Invoice date",
    width: 125,
    type: "date",
    valueGetter: (_, row) =>
      row.invoice.invoiceDate
        ? new Date(`${row.invoice.invoiceDate.slice(0, 10)}T00:00:00Z`)
        : null,
    valueFormatter: (value: Date | null) => formatInvoiceDate(value?.toISOString()),
  },
  { field: "vendorName", headerName: "Vendor", minWidth: 150, flex: 1 },
  {
    field: "invoiceNumber",
    sortable: false,
    headerName: "Invoice #",
    width: 130,
    valueGetter: (_, row) => row.invoice.invoiceNumber,
  },
  {
    field: "facilities",
    headerName: "Facilities",
    minWidth: 240,
    flex: 1.5,
    valueGetter: (_, row) => row.facilities.join(", ") || "Not provided",
    renderCell: ({ value }) => <TextCellWithTooltip>{value}</TextCellWithTooltip>,
  },
  {
    field: "billedAmount",
    headerName: "Billed amount",
    width: 140,
    align: "right",
    headerAlign: "right",
    type: "number",
    valueGetter: (_, row) => row.totals.billedAmount,
    renderCell: ({ row }) => formatAuditMoney("billedAmount", row.totals.billedAmount),
  },
  {
    field: "nonCompliantLineCount",
    headerName: "Mismatch lines",
    width: 150,
    type: "number",
    align: "right",
    headerAlign: "right",
    valueGetter: (_, row) => row.totals.nonCompliantLineCount,
  },
  {
    field: "unresolvedLineCount",
    headerName: "Unresolved lines",
    width: 150,
    type: "number",
    sortable: false,
    align: "right",
    headerAlign: "right",
    valueGetter: (_, row) => row.totals.unresolvedLineCount,
  },
  {
    field: "totalOverages",
    headerName: "Overages",
    width: 165,
    align: "right",
    headerAlign: "right",
    type: "number",
    valueGetter: (_, row) => (row.totals.calculatedLineCount > 0 ? row.totals.totalOverages : null),
    renderCell: ({ row }) =>
      formatAuditTotal(row.totals.calculatedLineCount, row.totals.totalOverages),
  },
  {
    field: "totalUnderages",
    headerName: "Underages",
    width: 165,
    align: "right",
    headerAlign: "right",
    type: "number",
    valueGetter: (_, row) =>
      row.totals.calculatedLineCount > 0 ? row.totals.totalUnderages : null,
    renderCell: ({ row }) =>
      formatAuditTotal(row.totals.calculatedLineCount, row.totals.totalUnderages),
  },
  {
    field: "variance",
    headerName: "Net variance",
    width: 165,
    align: "right",
    headerAlign: "right",
    type: "number",
    valueGetter: (_, row) => (row.totals.calculatedLineCount > 0 ? row.totals.variance : null),
    renderCell: ({ row }) => formatAuditTotal(row.totals.calculatedLineCount, row.totals.variance),
  },
];

type GridProps = Omit<
  SuspenseDataGridProps<InvoiceAuditReportQuery, InvoiceAuditReportQueryVariables, ReportRow>,
  "queryRef" | "getItems" | "getCount"
>;
const getItems = (data: InvoiceAuditReportQuery) => data.wasteInvoiceChargeAuditReport.items;
const getCount = (data: InvoiceAuditReportQuery) => data.wasteInvoiceChargeAuditReport.count;
const getRowId = (row: ReportRow) => row.invoice.id;

function ReportSummary({
  queryRef,
}: {
  queryRef: QueryRef<InvoiceAuditReportSummaryQuery, InvoiceAuditReportSummaryQueryVariables>;
}) {
  const { data } = useReadQuery(queryRef);
  return <AuditSummary summary={data.wasteInvoiceChargeAuditReportSummary.summary} />;
}

function ReportQuery({
  variables,
  gridProps,
  toolbar,
}: {
  variables: InvoiceAuditReportQueryVariables;
  gridProps: GridProps;
  toolbar: ReactNode;
}) {
  const {
    queryRef,
    retry,
    onRetry,
    summaryRef,
    summaryRetry,
    onSummaryRetry,
    isPending,
    summaryPending,
  } = useInvoiceAuditReportQueries(variables);
  return (
    <WasteListPageSurface
      headerOffset={INVOICE_TRACKING_HEADER_HEIGHT}
      above={
        <Box sx={{ mb: 3, opacity: summaryPending ? 0.6 : 1 }}>
          <AuditErrorBoundary
            key={summaryRetry}
            resetKey={summaryRef}
            onRetry={onSummaryRetry}
            fallback={null}
          >
            <Suspense fallback={<AuditSummary />}>
              {summaryRef ? <ReportSummary queryRef={summaryRef} /> : <AuditSummary />}
            </Suspense>
          </AuditErrorBoundary>
        </Box>
      }
    >
      <Box sx={{ px: 3, mb: 2 }}>
        <InvoiceTrackingListHeading title="Invoice Audit">
          Each row is one invoice, with its lines compared against the rates agreed with that
          vendor. Overages and underages are what the comparison found.
        </InvoiceTrackingListHeading>
      </Box>
      <Box sx={{ px: 3 }}>{toolbar}</Box>
      {/* The surface leaves the horizontal inset to its contents, because
          OmnisearchDataGridV2 insets itself. This grid is a SuspenseDataGrid and
          does not, so it is padded to line up with the heading and toolbar. */}
      <Box sx={{ px: 3, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <AuditErrorBoundary key={retry} resetKey={queryRef} onRetry={onRetry}>
          <SuspenseDataGrid
            {...gridProps}
            queryRef={queryRef}
            isPending={isPending}
            getItems={getItems}
            getCount={getCount}
            fallback={<DataGridSkeleton />}
          />
        </AuditErrorBoundary>
      </Box>
    </WasteListPageSurface>
  );
}

export function InvoiceAuditReport() {
  const { tenantId } = useTenant();
  const { omnisearch } = useOmnisearchStore();
  const actions = useOmnisearchActions();
  const hiddenDisplayTokens = [...usePeriodDisplayTokens()];
  const persistence = useGridPersistence({
    persistenceKey: PERSISTENCE_KEY,
    initialSortModel: INITIAL_SORT,
    initialPageSize: 25,
    columns: COLUMNS,
  });
  const { page, setPage } = usePageForSearch(
    JSON.stringify([omnisearch, persistence.sortModel, persistence.pageSize])
  );
  const columns = useMemo(() => COLUMNS.map((column) => ({ ...column, filterable: false })), []);
  const variables = useMemo(
    (): InvoiceAuditReportQueryVariables => ({
      tenantId: tenantId ?? "",
      search: omnisearch,
      page,
      pageSize: persistence.pageSize,
      sort: persistence.sortModel.map(({ field, sort }) => ({ field, sort })),
    }),
    [tenantId, omnisearch, page, persistence.pageSize, persistence.sortModel]
  );
  const base = `/o/${tenantId}/waste/invoices`;
  const getRowLink = useCallback(
    (row: ReportRow) => `${base}/audit/${row.invoice.id}?${new URLSearchParams({ omnisearch })}`,
    [base, omnisearch]
  );
  const paginationModel = useMemo(
    () => ({ page, pageSize: persistence.pageSize }),
    [page, persistence.pageSize]
  );
  const { setPageSize } = persistence;
  const onPaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      setPage(model.page);
      setPageSize(model.pageSize);
    },
    [setPage, setPageSize]
  );
  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Invoice Tracking", to: base },
    { label: "Audit" },
  ]);
  if (!tenantId) return null;
  const gridProps: GridProps = {
    columns,
    getRowId,
    getRowLink,
    paginationModel,
    onPaginationModelChange,
    sortModel: persistence.sortModel,
    onSortModelChange: persistence.setSortModel,
    columnVisibilityModel: persistence.columnVisibilityModel,
    onColumnVisibilityModelChange: persistence.setColumnVisibilityModel,
    pageSizeOptions: PAGE_SIZES,
    disableRowSelectionOnClick: true,
    noRowsMessage: "No invoices match this reporting period and filters.",
  };
  return (
    <ReportQuery
      variables={variables}
      gridProps={gridProps}
      toolbar={
        <DataGridToolbar
          compact
          tenantId={tenantId}
          persistenceKey={PERSISTENCE_KEY}
          omnisearchSource={{ omnisearch, ...actions }}
          columnFilterKeys={FILTER_KEYS}
          filterGroups={FILTER_GROUPS}
          preservedFilterKeys={PRESERVED_FILTER_KEYS}
          hiddenDisplayTokens={hiddenDisplayTokens}
          columns={columns}
          columnVisibilityModel={persistence.columnVisibilityModel}
          onColumnVisibilityModelChange={persistence.setColumnVisibilityModel}
        />
      }
    />
  );
}
